// ===========================================
// School events strip (Day resource view, #256)
// ===========================================
// A school event with no room belongs to no teacher column, so in the Day view's resource columns
// it has nowhere to draw. This strip lists the day's events above the columns so a concert is never
// invisible. An event HELD IN A ROOM draws in that room's column in room mode, so the strip leaves it
// out there (one place only); in teacher mode it has no column, so the strip lists it.

import { Box, Chip, Typography } from '@mui/material';
import { format } from 'date-fns';
import type { CalendarEvent } from '../../../api/hybridBooking.api';
import { eventTimeLabelForDay } from '../../events/eventCalendarItem';
import { EVENT_CALENDAR_COLOR } from '../../../constants/eventCalendar';

interface CalendarEventsStripProps {
  events: CalendarEvent[];
  /** The day shown (local midnight; calendar items are fake-local too). */
  date: Date;
  /** Which resource columns the Day view draws. */
  resourceViewMode?: 'teacher' | 'room';
  onSelect: (event: CalendarEvent) => void;
}

export function eventsOnDay(
  events: CalendarEvent[],
  date: Date,
  resourceViewMode: 'teacher' | 'room' = 'teacher'
): CalendarEvent[] {
  const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
  const dayEnd = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
  return events.filter(
    (e) =>
      e.resource?.type === 'EVENT' &&
      // In room mode an event with a room already draws in that room's column.
      !(resourceViewMode === 'room' && e.resource.roomId) &&
      new Date(e.start).getTime() <= dayEnd.getTime() &&
      new Date(e.end).getTime() >= dayStart.getTime()
  );
}

export default function CalendarEventsStrip({ events, date, resourceViewMode, onSelect }: CalendarEventsStripProps) {
  const dayEvents = eventsOnDay(events, date, resourceViewMode);
  if (dayEvents.length === 0) return null;
  const dayKey = format(date, 'yyyy-MM-dd');
  return (
    <Box
      data-testid="calendar-events-strip"
      sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1, minWidth: 0 }}
    >
      <Typography variant="caption" color="text.secondary">
        School events
      </Typography>
      {dayEvents.map((e) => {
        const cancelled = e.resource?.isCancelled === true;
        const when = eventTimeLabelForDay(dayKey, e.dateKey, e.endDateKey, e.startTime, e.endTime);
        return (
          <Chip
            key={e.id}
            size="small"
            onClick={() => onSelect(e)}
            label={`${when} ${e.title}${cancelled ? ' (cancelled)' : ''}`}
            sx={{
              backgroundColor: EVENT_CALENDAR_COLOR,
              color: '#080808',
              textDecoration: cancelled ? 'line-through' : undefined,
              maxWidth: '100%',
            }}
          />
        );
      })}
    </Box>
  );
}
